import React from 'react';
import { toUrl } from '../lib/config';

type BashekimMesaji = {
  id: string;
  greeting?: string | null;
  content?: string | null;
  closing?: string | null;
  signature?: string | null;
  title?: string | null;
  imageUrl?: string | null;
  doctorName?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type Media = {
  id: string;
  name: string;
  url: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  isActive: boolean;
};

const BashekimMesajiYonetim: React.FC = () => {
  const [messages, setMessages] = React.useState<BashekimMesaji[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const [greeting, setGreeting] = React.useState('');
  const [content, setContent] = React.useState('');
  const [closing, setClosing] = React.useState('');
  const [signature, setSignature] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [doctorName, setDoctorName] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [toast, setToast] = React.useState<{ show: boolean; text: string }>({ show: false, text: '' });
  const [mediaList, setMediaList] = React.useState<Media[]>([]);

  const fetchAll = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl('/api/bashekim-mesaji/admin'));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setMessages(data || []);
      const initial = (data && data[0]) || null;
      if (initial) {
        setSelectedId(initial.id);
        setGreeting(initial.greeting || '');
        setContent(initial.content || '');
        setClosing(initial.closing || '');
        setSignature(initial.signature || '');
        setTitle(initial.title || '');
        setImageUrl(initial.imageUrl || '');
        setDoctorName(initial.doctorName || '');
        setIsActive(!!initial.isActive);
      }
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      const items = data.data || data || [];
      setMediaList(items);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchAll();
    fetchMedia();
  }, [fetchAll, fetchMedia]);

  const onChangeSelection = (id: string) => {
    setSelectedId(id);
    const msg = messages.find(x => x.id === id);
    if (msg) {
      setGreeting(msg.greeting || '');
      setContent(msg.content || '');
      setClosing(msg.closing || '');
      setSignature(msg.signature || '');
      setTitle(msg.title || '');
      setImageUrl(msg.imageUrl || '');
      setDoctorName(msg.doctorName || '');
      setIsActive(!!msg.isActive);
    }
  };

  const onSave = async () => {
    if (!selectedId) {
      setError('Düzenlenecek kayıt bulunamadı');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const body: any = {
        greeting: greeting || null,
        content: content || null,
        closing: closing || null,
        signature: signature || null,
        title: title || null,
        imageUrl: imageUrl || null,
        doctorName: doctorName || null,
        isActive: !!isActive,
      };
      const res = await fetch(toUrl(`/api/bashekim-mesaji/admin/${selectedId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      await fetchAll();
      setToast({ show: true, text: 'Başarıyla kaydedildi!' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const onCreate = async () => {
    try {
      setSaving(true);
      setError('');
      const body: any = {
        greeting: greeting || null,
        content: content || null,
        closing: closing || null,
        signature: signature || null,
        title: title || null,
        imageUrl: imageUrl || null,
        doctorName: doctorName || null,
        isActive: !!isActive,
      };
      const res = await fetch(toUrl('/api/bashekim-mesaji/admin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      await fetchAll();
      setToast({ show: true, text: 'Yeni kayıt oluşturuldu!' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      setSaving(true);
      setError('');
      const res = await fetch(toUrl(`/api/bashekim-mesaji/admin/${selectedId}`), {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Silme başarısız');
      await fetchAll();
      setToast({ show: true, text: 'Kayıt silindi!' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Başhekim Mesajı Yönetimi</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Kayıt: {messages.length}</div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Mesaj Alanları</div>
          <button
            onClick={fetchAll}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            Yenile
          </button>
        </div>

        {messages.length > 1 && (
          <div style={{ marginBottom: 16 }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>
              Kayıt Seç
            </label>
            <select className="input" value={selectedId} onChange={(e) => onChangeSelection(e.target.value)}>
              {messages.map((msg) => (
                <option key={msg.id} value={msg.id}>
                  {msg.greeting || msg.title || msg.id}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Selamlama (Greeting)</label>
            <input
              className="input"
              placeholder="Örn: Değerli Hastalarımız,"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">İçerik (Content)</label>
            <textarea
              className="input"
              rows={8}
              placeholder="Mesaj içeriği... (Paragrafları ayırmak için çift satır boşluğu kullanın)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <small style={{ color: '#6b7280' }}>
              Not: Paragrafları ayırmak için iki kez Enter'a basın (çift satır boşluğu)
            </small>
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kapanış (Closing)</label>
            <input
              className="input"
              placeholder="Örn: Sağlıklı günler dilerim."
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <label className="form-label">İmza (Signature)</label>
              <input
                className="input"
                placeholder="Örn: Saygılarımla,"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
              />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label className="form-label">Ünvan (Title)</label>
              <input
                className="input"
                placeholder="Örn: Başhekim"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Resim (imageUrl)</label>
            <select
              className="input"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            >
              <option value="">Resim Seçiniz</option>
              {mediaList
                .filter(m => (m.mime || '').startsWith('image/'))
                .map(m => (
                  <option key={m.id} value={`/api/media/${m.id}`}>
                    {m.name || m.filename}
                  </option>
                ))}
            </select>
            {imageUrl && (
              <div className="mt-2">
                <img
                  src={toUrl(imageUrl)}
                  alt="Preview"
                  style={{ height: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Başhekimin Adı (Doctor Name)</label>
            <input
              className="input"
              placeholder="Örn: Dr. Mehmet Yılmaz"
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              id="act"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor="act">Aktif</label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
          <button
            onClick={onSave}
            disabled={saving || !selectedId}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: '1px solid #16a34a',
              background: '#16a34a',
              color: '#fff',
              cursor: 'pointer',
              opacity: saving || !selectedId ? 0.8 : 1,
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>

        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>

      {toast.show && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            background: '#16a34a',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default BashekimMesajiYonetim;
